/**
 * Privacy policy page (Politique de confidentialité).
 *
 * Comprehensive GDPR-compliant privacy policy for library patrons.
 * Details data collection, processing purposes, retention periods,
 * and rights of data subjects.
 *
 * IMPORTANT: This page contains placeholder values that MUST be customized
 * for each deployment. See docs/operations/legal-configuration.md.
 */

import { Link } from "react-router-dom";

/**
 * Configuration for privacy policy content.
 * These values should come from environment variables or CMS in production.
 */
const PRIVACY_CONFIG = {
  // Responsable de traitement
  controller: {
    name: "[Nom de la collectivité]",
    address: "[Adresse complète]",
    email: "[Email de contact]",
  },
  // DPO
  dpo: {
    name: "[Nom du DPO ou service]",
    email: "[Email du DPO]",
    address: "[Adresse pour exercice des droits]",
  },
  // Durées de conservation
  retention: {
    activeAccount: "pendant toute la durée de l'inscription",
    inactiveAccount: "3 ans après la dernière activité",
    loanHistory: "1 an après le retour du document",
    statistics: "5 ans (données anonymisées)",
    logs: "1 an",
  },
};

export function PrivacyPolicyPage() {
  return (
    <div className="mx-auto max-w-4xl">
      <nav aria-label="Fil d'Ariane" className="mb-6">
        <ol className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <li>
            <Link
              to="/opac"
              className="hover:text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none dark:hover:text-blue-400"
            >
              Accueil
            </Link>
          </li>
          <li aria-hidden="true">/</li>
          <li aria-current="page" className="font-medium text-gray-900 dark:text-white">
            Politique de confidentialité
          </li>
        </ol>
      </nav>

      <article className="prose prose-gray dark:prose-invert max-w-none">
        <h1>Politique de confidentialité</h1>

        <p className="lead">
          La protection de vos données personnelles est une priorité pour notre bibliothèque. Cette
          politique vous informe sur la manière dont nous collectons, utilisons et protégeons vos
          données conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi
          Informatique et Libertés.
        </p>

        <nav aria-label="Sommaire">
          <h2>Sommaire</h2>
          <ol>
            <li>
              <a href="#responsable">Responsable du traitement</a>
            </li>
            <li>
              <a href="#donnees-collectees">Données collectées</a>
            </li>
            <li>
              <a href="#finalites">Finalités du traitement</a>
            </li>
            <li>
              <a href="#base-legale">Base légale</a>
            </li>
            <li>
              <a href="#destinataires">Destinataires des données</a>
            </li>
            <li>
              <a href="#conservation">Durées de conservation</a>
            </li>
            <li>
              <a href="#droits">Vos droits</a>
            </li>
            <li>
              <a href="#securite">Sécurité</a>
            </li>
            <li>
              <a href="#cookies">Cookies</a>
            </li>
            <li>
              <a href="#modifications">Modifications</a>
            </li>
          </ol>
        </nav>

        <section id="responsable">
          <h2>1. Responsable du traitement</h2>
          <p>Le responsable du traitement de vos données personnelles est :</p>
          <address className="not-italic">
            <strong>{PRIVACY_CONFIG.controller.name}</strong>
            <br />
            {PRIVACY_CONFIG.controller.address}
            <br />
            Email :{" "}
            <a href={`mailto:${PRIVACY_CONFIG.controller.email}`}>
              {PRIVACY_CONFIG.controller.email}
            </a>
          </address>
          <p>
            Pour toute question relative à la protection de vos données, vous pouvez contacter notre
            Délégué à la Protection des Données (DPO) :
          </p>
          <address className="not-italic">
            <strong>{PRIVACY_CONFIG.dpo.name}</strong>
            <br />
            Email : <a href={`mailto:${PRIVACY_CONFIG.dpo.email}`}>{PRIVACY_CONFIG.dpo.email}</a>
            <br />
            Adresse : {PRIVACY_CONFIG.dpo.address}
          </address>
        </section>

        <section id="donnees-collectees">
          <h2>2. Données collectées</h2>
          <p>
            Dans le cadre de nos services de bibliothèque, nous collectons les catégories de données
            suivantes :
          </p>

          <h3>Données d'identification</h3>
          <ul>
            <li>Nom et prénom</li>
            <li>Date de naissance (pour déterminer la catégorie de lecteur)</li>
            <li>Genre (facultatif)</li>
            <li>Numéro de carte de bibliothèque</li>
          </ul>

          <h3>Coordonnées</h3>
          <ul>
            <li>Adresse postale</li>
            <li>Adresse email</li>
            <li>Numéro de téléphone (fixe et/ou mobile)</li>
          </ul>

          <h3>Données relatives aux prêts</h3>
          <ul>
            <li>Documents empruntés (titre, auteur, type)</li>
            <li>Dates de prêt et de retour</li>
            <li>Réservations en cours et passées</li>
            <li>Éventuelles pénalités de retard</li>
          </ul>

          <h3>Données de connexion</h3>
          <ul>
            <li>Identifiant de connexion</li>
            <li>Date et heure de dernière connexion</li>
            <li>Préférences de notification</li>
          </ul>
        </section>

        <section id="finalites">
          <h2>3. Finalités du traitement</h2>
          <p>Vos données sont traitées pour les finalités suivantes :</p>

          <table>
            <thead>
              <tr>
                <th>Finalité</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <strong>Gestion des inscriptions</strong>
                </td>
                <td>Création et suivi de votre compte lecteur, attribution de votre carte</td>
              </tr>
              <tr>
                <td>
                  <strong>Gestion des prêts</strong>
                </td>
                <td>Enregistrement des emprunts, retours, prolongations et réservations</td>
              </tr>
              <tr>
                <td>
                  <strong>Communication</strong>
                </td>
                <td>
                  Notifications de disponibilité, rappels de retour, informations sur nos services
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Statistiques</strong>
                </td>
                <td>
                  Analyse anonymisée de la fréquentation et des emprunts pour améliorer nos services
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Recouvrement</strong>
                </td>
                <td>Gestion des pénalités de retard et des documents non restitués</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section id="base-legale">
          <h2>4. Base légale du traitement</h2>
          <p>Le traitement de vos données repose sur les bases légales suivantes :</p>
          <ul>
            <li>
              <strong>Exécution du contrat</strong> : votre inscription à la bibliothèque constitue
              un contrat de service public. Le traitement de vos données est nécessaire à
              l'exécution de ce service (prêts, réservations).
            </li>
            <li>
              <strong>Obligation légale</strong> : certains traitements sont requis par la
              réglementation (conservation des données comptables pour les pénalités).
            </li>
            <li>
              <strong>Intérêt légitime</strong> : l'amélioration de nos services et la prévention
              des fraudes constituent un intérêt légitime.
            </li>
            <li>
              <strong>Consentement</strong> : pour l'envoi de communications non essentielles
              (newsletters, événements), nous recueillons votre consentement explicite.
            </li>
          </ul>
        </section>

        <section id="destinataires">
          <h2>5. Destinataires des données</h2>
          <p>Vos données sont accessibles uniquement aux personnes habilitées :</p>
          <ul>
            <li>Le personnel de la bibliothèque, dans la limite de leurs attributions</li>
            <li>Les services informatiques pour la maintenance du système</li>
            <li>
              Les prestataires techniques (hébergement), liés par des clauses de confidentialité
            </li>
          </ul>
          <p>
            <strong>Aucune donnée n'est transmise à des tiers à des fins commerciales.</strong>
          </p>
          <p>
            Vos données sont hébergées en France / Union européenne et ne font l'objet d'aucun
            transfert hors de l'Espace économique européen.
          </p>
        </section>

        <section id="conservation">
          <h2>6. Durées de conservation</h2>
          <p>
            Vos données sont conservées pendant les durées suivantes, conformément aux
            recommandations de la CNIL et aux obligations légales :
          </p>

          <table>
            <thead>
              <tr>
                <th>Type de données</th>
                <th>Durée de conservation</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Données du compte actif</td>
                <td>{PRIVACY_CONFIG.retention.activeAccount}</td>
              </tr>
              <tr>
                <td>Compte inactif</td>
                <td>{PRIVACY_CONFIG.retention.inactiveAccount}</td>
              </tr>
              <tr>
                <td>Historique des prêts</td>
                <td>{PRIVACY_CONFIG.retention.loanHistory}</td>
              </tr>
              <tr>
                <td>Données statistiques</td>
                <td>{PRIVACY_CONFIG.retention.statistics}</td>
              </tr>
              <tr>
                <td>Journaux de connexion</td>
                <td>{PRIVACY_CONFIG.retention.logs}</td>
              </tr>
            </tbody>
          </table>

          <p>
            Au terme de ces durées, vos données sont supprimées ou anonymisées de manière
            irréversible.
          </p>
        </section>

        <section id="droits">
          <h2>7. Vos droits</h2>
          <p>
            Conformément au RGPD, vous disposez des droits suivants sur vos données personnelles :
          </p>

          <dl>
            <dt>
              <strong>Droit d'accès</strong>
            </dt>
            <dd>Obtenir une copie de l'ensemble de vos données personnelles détenues par nous.</dd>

            <dt>
              <strong>Droit de rectification</strong>
            </dt>
            <dd>Faire corriger vos données inexactes ou incomplètes.</dd>

            <dt>
              <strong>Droit à l'effacement</strong>
            </dt>
            <dd>
              Demander la suppression de vos données, sous réserve des obligations légales de
              conservation.
            </dd>

            <dt>
              <strong>Droit à la limitation</strong>
            </dt>
            <dd>Demander la suspension temporaire du traitement de vos données.</dd>

            <dt>
              <strong>Droit à la portabilité</strong>
            </dt>
            <dd>
              Recevoir vos données dans un format structuré et couramment utilisé (JSON, CSV).
            </dd>

            <dt>
              <strong>Droit d'opposition</strong>
            </dt>
            <dd>
              Vous opposer au traitement de vos données pour des motifs légitimes, ou à la
              prospection.
            </dd>

            <dt>
              <strong>Directives post-mortem</strong>
            </dt>
            <dd>
              Définir des directives relatives à la conservation et à la communication de vos
              données après votre décès.
            </dd>
          </dl>

          <h3>Comment exercer vos droits ?</h3>
          <p>Pour exercer vos droits, vous pouvez :</p>
          <ul>
            <li>
              Vous connecter à votre compte et accéder à la rubrique{" "}
              <Link to="/opac/account">« Mon compte » &gt; « Mes données personnelles »</Link>
            </li>
            <li>
              Contacter notre DPO par email à{" "}
              <a href={`mailto:${PRIVACY_CONFIG.dpo.email}`}>{PRIVACY_CONFIG.dpo.email}</a>
            </li>
            <li>Adresser un courrier à : {PRIVACY_CONFIG.dpo.address}</li>
          </ul>
          <p>
            Nous nous engageons à répondre à votre demande dans un délai d'un mois. Ce délai peut
            être prolongé de deux mois en cas de demande complexe.
          </p>

          <h3>Réclamation auprès de la CNIL</h3>
          <p>
            Si vous estimez que vos droits ne sont pas respectés après nous avoir contactés, vous
            pouvez introduire une réclamation auprès de la Commission Nationale de l'Informatique et
            des Libertés (CNIL) :
          </p>
          <address className="not-italic">
            <strong>CNIL</strong>
            <br />
            3 Place de Fontenoy - TSA 80715
            <br />
            75334 PARIS CEDEX 07
            <br />
            <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer">
              www.cnil.fr
            </a>
          </address>
        </section>

        <section id="securite">
          <h2>8. Sécurité des données</h2>
          <p>
            Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour
            protéger vos données :
          </p>
          <ul>
            <li>Chiffrement des communications (HTTPS/TLS)</li>
            <li>Chiffrement des mots de passe (algorithme bcrypt)</li>
            <li>Contrôle d'accès strict aux données</li>
            <li>Journalisation des accès aux données sensibles</li>
            <li>Sauvegardes régulières et sécurisées</li>
            <li>Formation du personnel aux bonnes pratiques</li>
          </ul>
        </section>

        <section id="cookies">
          <h2>9. Cookies</h2>
          <p>
            Ce site utilise uniquement des cookies strictement nécessaires au fonctionnement du
            service :
          </p>

          <table>
            <thead>
              <tr>
                <th>Cookie</th>
                <th>Finalité</th>
                <th>Durée</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <code>session_id</code>
                </td>
                <td>Maintien de la session de connexion</td>
                <td>Session (supprimé à la fermeture du navigateur)</td>
              </tr>
              <tr>
                <td>
                  <code>auth_token</code>
                </td>
                <td>Authentification sécurisée</td>
                <td>7 jours ou jusqu'à déconnexion</td>
              </tr>
              <tr>
                <td>
                  <code>theme</code>
                </td>
                <td>Préférence d'affichage (clair/sombre)</td>
                <td>1 an</td>
              </tr>
            </tbody>
          </table>

          <p>
            Ces cookies étant strictement nécessaires à la fourniture du service, ils ne nécessitent
            pas votre consentement préalable conformément à la directive ePrivacy.
          </p>
          <p>
            Aucun cookie de mesure d'audience, de publicité ou de réseau social n'est utilisé sur ce
            site.
          </p>
        </section>

        <section id="modifications">
          <h2>10. Modifications de cette politique</h2>
          <p>
            Cette politique de confidentialité peut être mise à jour pour refléter les évolutions de
            nos pratiques ou de la réglementation. En cas de modification substantielle, nous vous
            en informerons par email ou par une notification sur le site.
          </p>
          <p>Nous vous invitons à consulter régulièrement cette page pour rester informé.</p>
        </section>

        <footer className="mt-8 border-t border-gray-200 pt-4 text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
          <p>Dernière mise à jour : {new Date().toLocaleDateString("fr-FR")}</p>
          <p>
            <Link to="/opac/mentions-legales">Voir les mentions légales</Link>
          </p>
        </footer>
      </article>
    </div>
  );
}

export default PrivacyPolicyPage;
